import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { PLATFORMS } from '../components/common/PlatformIcon';
import { postsApi, mediaApi, aiApi } from '../lib/api';
import { ArrowLeft, Save, Send, CalendarDays, Image, X, Wand2, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export default function PostEditor() {
  const { companyId } = useOutletContext();
  const { postId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!postId;

  const [form, setForm] = useState({
    title: '',
    caption: '',
    hashtags: '',
    cta_link: '',
    platform_targets: [],
    media_ids: [],
    status: 'draft',
    post_type: 'text',
    scheduled_at: '',
  });
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [aiCaptionLoading, setAiCaptionLoading] = useState(false);
  const [aiImageLoading, setAiImageLoading] = useState(false);

  // Fetch post if editing
  const { data: postData } = useQuery({
    queryKey: ['post', companyId, postId],
    queryFn: () => postsApi.get(companyId, postId).then((r) => r.data),
    enabled: isEdit,
  });

  // Fetch media library
  const { data: mediaData } = useQuery({
    queryKey: ['media', companyId],
    queryFn: () => mediaApi.list(companyId, { limit: 50 }).then((r) => r.data),
  });

  useEffect(() => {
    if (postData && isEdit) {
      setForm({
        title: postData.title || '',
        caption: postData.caption || '',
        hashtags: postData.hashtags?.join(', ') || '',
        cta_link: postData.cta_link || '',
        platform_targets: postData.platform_targets || [],
        media_ids: postData.media_ids || [],
        status: postData.status || 'draft',
        post_type: postData.post_type || 'text',
        scheduled_at: postData.scheduled_at ? postData.scheduled_at.substring(0, 16) : '',
      });
      setSelectedMedia(postData.media || []);
    }
  }, [postData, isEdit]);

  const saveMutation = useMutation({
    mutationFn: (data) => {
      const payload = {
        ...data,
        hashtags: data.hashtags ? data.hashtags.split(',').map((h) => h.trim()).filter(Boolean) : [],
        scheduled_at: data.scheduled_at || null,
      };
      if (isEdit) {
        return postsApi.update(companyId, postId, payload);
      }
      return postsApi.create(companyId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts', companyId] });
      toast.success(isEdit ? 'Post updated' : 'Post created');
      navigate(`/companies/${companyId}/posts`);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to save'),
  });

  const publishMutation = useMutation({
    mutationFn: () => {
      if (isEdit) {
        return postsApi.publish(companyId, postId);
      }
      // Create first, then publish
      const payload = {
        ...form,
        hashtags: form.hashtags ? form.hashtags.split(',').map((h) => h.trim()).filter(Boolean) : [],
        scheduled_at: form.scheduled_at || null,
      };
      return postsApi.create(companyId, payload).then((res) => {
        return postsApi.publish(companyId, res.data._id);
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts', companyId] });
      toast.success('Post published!');
      navigate(`/companies/${companyId}/posts`);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Publish failed'),
  });

  const togglePlatform = (platform) => {
    setForm((prev) => {
      const targets = prev.platform_targets.includes(platform)
        ? prev.platform_targets.filter((p) => p !== platform)
        : [...prev.platform_targets, platform];
      return { ...prev, platform_targets: targets };
    });
  };

  const toggleMedia = (mediaItem) => {
    const isSelected = form.media_ids.includes(mediaItem._id);
    if (isSelected) {
      setForm((prev) => ({ ...prev, media_ids: prev.media_ids.filter((id) => id !== mediaItem._id) }));
      setSelectedMedia((prev) => prev.filter((m) => m._id !== mediaItem._id));
    } else {
      setForm((prev) => ({ ...prev, media_ids: [...prev.media_ids, mediaItem._id] }));
      setSelectedMedia((prev) => [...prev, mediaItem]);
    }
  };

  const handleSave = (status) => {
    if (!form.caption.trim()) {
      toast.error('Caption is required');
      return;
    }
    if (form.platform_targets.length === 0) {
      toast.error('Select at least one platform');
      return;
    }
    if (status === 'scheduled' && !form.scheduled_at) {
      toast.error('Please set a schedule date/time');
      return;
    }
    saveMutation.mutate({ ...form, status });
  };

  const handleAiCaption = async () => {
    if (!form.title && !form.caption) {
      toast.error('Enter a title or some text first to generate a caption');
      return;
    }
    setAiCaptionLoading(true);
    try {
      const prompt = form.title || form.caption;
      const platform = form.platform_targets?.[0] || '';
      const res = await aiApi.generateCaption(companyId, { prompt, platform, language: 'auto' });
      setForm((p) => ({
        ...p,
        caption: res.data.caption || p.caption,
        hashtags: res.data.hashtags?.join(', ') || p.hashtags,
      }));
      toast.success('AI caption generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Caption generation failed');
    } finally {
      setAiCaptionLoading(false);
    }
  };

  const handleAiImage = async () => {
    const prompt = form.title || form.caption || 'social media promotional visual';
    setAiImageLoading(true);
    try {
      const res = await aiApi.generateImage(companyId, { prompt, save_to_library: true });
      if (res.data.media_asset) {
        setForm((p) => ({ ...p, media_ids: [...p.media_ids, res.data.media_asset._id] }));
        setSelectedMedia((prev) => [...prev, res.data.media_asset]);
        queryClient.invalidateQueries({ queryKey: ['media', companyId] });
      }
      toast.success('AI image generated and attached!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image generation failed');
    } finally {
      setAiImageLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/companies/${companyId}/posts`)}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h2 className="text-xl font-semibold font-display">{isEdit ? 'Edit Post' : 'Create Post'}</h2>
      </div>

      <div className="space-y-6">
        {/* Title */}
        <div className="space-y-2">
          <Label>Title (optional)</Label>
          <Input
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            placeholder="Post title"
            data-testid="post-editor-title-input"
          />
        </div>

        {/* Caption */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Caption *</Label>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAiCaption}
              disabled={aiCaptionLoading}
              className="text-primary"
              data-testid="ai-caption-button"
            >
              {aiCaptionLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              AI Caption
            </Button>
          </div>
          <Textarea
            value={form.caption}
            onChange={(e) => setForm((p) => ({ ...p, caption: e.target.value }))}
            placeholder="Write your post content..."
            rows={5}
            data-testid="post-editor-content-textarea"
          />
          <p className="text-xs text-muted-foreground">{form.caption.length} characters</p>
        </div>

        {/* Platforms */}
        <div className="space-y-2">
          <Label>Target Platforms *</Label>
          <div className="flex flex-wrap gap-2" data-testid="post-editor-platform-select">
            {PLATFORMS.map((p) => (
              <label
                key={p.value}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors duration-150 ${
                  form.platform_targets.includes(p.value)
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border hover:bg-muted'
                }`}
              >
                <Checkbox
                  checked={form.platform_targets.includes(p.value)}
                  onCheckedChange={() => togglePlatform(p.value)}
                />
                <span className="text-sm">{p.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Post Type */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Post Type</Label>
            <Select value={form.post_type} onValueChange={(v) => setForm((p) => ({ ...p, post_type: v }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="image">Image</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="carousel">Carousel</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Schedule Date/Time</Label>
            <Input
              type="datetime-local"
              value={form.scheduled_at}
              onChange={(e) => setForm((p) => ({ ...p, scheduled_at: e.target.value }))}
              data-testid="post-editor-schedule-input"
            />
          </div>
        </div>

        {/* Hashtags */}
        <div className="space-y-2">
          <Label>Hashtags</Label>
          <Input
            value={form.hashtags}
            onChange={(e) => setForm((p) => ({ ...p, hashtags: e.target.value }))}
            placeholder="#marketing, #social, #content"
          />
          <p className="text-xs text-muted-foreground">Separate with commas</p>
        </div>

        {/* CTA Link */}
        <div className="space-y-2">
          <Label>CTA / Link</Label>
          <Input
            value={form.cta_link}
            onChange={(e) => setForm((p) => ({ ...p, cta_link: e.target.value }))}
            placeholder="https://example.com"
          />
        </div>

        {/* Media Attachment */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Media</Label>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAiImage}
              disabled={aiImageLoading}
              className="text-primary"
              data-testid="ai-image-button"
            >
              {aiImageLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              AI Image
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedMedia.map((media) => (
              <div key={media._id} className="relative w-20 h-20 rounded-lg overflow-hidden bg-muted group">
                {media.file_type === 'image' ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${media.path_or_url}`}
                    alt={media.filename}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-6 h-6 text-muted-foreground" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => toggleMedia(media)}
                  className="absolute top-1 right-1 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <Dialog open={mediaDialogOpen} onOpenChange={setMediaDialogOpen}>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="w-20 h-20 rounded-lg border-2 border-dashed flex items-center justify-center hover:bg-muted transition-colors"
                  data-testid="attach-media-button"
                >
                  <Image className="w-5 h-5 text-muted-foreground" />
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Select Media</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-4 gap-3 max-h-96 overflow-y-auto p-1">
                  {mediaData?.media?.map((item) => (
                    <button
                      key={item._id}
                      type="button"
                      onClick={() => toggleMedia(item)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                        form.media_ids.includes(item._id)
                          ? 'border-primary ring-2 ring-primary/30'
                          : 'border-transparent hover:border-muted-foreground/30'
                      }`}
                    >
                      {item.file_type === 'image' ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${item.path_or_url}`}
                          alt={item.filename}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <Image className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                    </button>
                  ))}
                  {(!mediaData?.media || mediaData.media.length === 0) && (
                    <p className="col-span-4 text-center text-sm text-muted-foreground py-8">
                      No media files. Upload some in the Media Library first.
                    </p>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pt-4 border-t">
          <Button
            onClick={() => handleSave('draft')}
            variant="secondary"
            disabled={saveMutation.isPending}
            data-testid="save-draft-button"
          >
            <Save className="w-4 h-4 mr-2" /> Save Draft
          </Button>
          <Button
            onClick={() => handleSave('scheduled')}
            variant="secondary"
            disabled={saveMutation.isPending || !form.scheduled_at}
            data-testid="post-editor-schedule-button"
          >
            <CalendarDays className="w-4 h-4 mr-2" /> Schedule
          </Button>
          <Button
            onClick={() => {
              if (isEdit) {
                publishMutation.mutate();
              } else {
                if (!form.caption.trim() || form.platform_targets.length === 0) {
                  toast.error('Caption and platforms are required');
                  return;
                }
                publishMutation.mutate();
              }
            }}
            disabled={publishMutation.isPending}
            data-testid="post-editor-publish-button"
          >
            <Send className="w-4 h-4 mr-2" /> Publish Now
          </Button>
        </div>
      </div>
    </div>
  );
}
